/**
 * Market API — mirrors smt_market_handler.go.
 *
 *   GET /api/v1/smt/market/overview?region=&max_age_months=
 *   GET /api/v1/smt/market/inst-interest?maakunta=&size_ha=
 *
 * Both endpoints are aggregate-only (no per-row PII) and reachable for
 * anonymous visitors — the auth middleware on the route group is
 * `OptionalJWTAuth`.
 */
import { requestData } from './client.ts';

export interface MarketBucket {
  year: number;
  n_sales: number;
  median_eur_ha: number;
}

export interface MarketSizeBucket {
  label: string;
  min_ha: number;
  max_ha: number;
  n_sales: number;
  median_eur_ha: number;
}

export interface MarketOverview {
  region: string;
  n_sales: number;
  median_eur_ha: number;
  median_eur_m3: number;
  median_ha: number;
  median_m3_per_ha: number;
  p25_eur_ha: number;
  p75_eur_ha: number;
  buckets: MarketBucket[];
  size_buckets: MarketSizeBucket[];
}

export interface InstCutoff {
  threshold: number;
  inst_pct: number;
  n: number;
}

export interface InstInterestResponse {
  maakunta: string;
  n_total: number;
  overall_inst_pct: number;
  pattern: 'size_dependent' | 'private_dominant';
  effective_threshold?: number | null;
  cutoffs: InstCutoff[];
  // Filled when size_ha is sent in the query.
  pct?: number;
  sample_n?: number;
  used_threshold?: number;
  category?: 'inst_strong' | 'inst_likely' | 'borderline' | 'inst_unlikely' | 'private' | 'unknown';
}

/** Fetch country- or region-level market overview. */
export async function getMarketOverview(opts: { region?: string; maxAgeMonths?: number } = {}): Promise<MarketOverview> {
  return requestData<MarketOverview>('/smt/market/overview', {
    query: {
      region: opts.region,
      max_age_months: opts.maxAgeMonths,
    },
  });
}

/** Fetch institutional-buyer share for a single maakunta. */
export async function getInstInterest(maakunta: string, sizeHa?: number): Promise<InstInterestResponse> {
  return requestData<InstInterestResponse>('/smt/market/inst-interest', {
    query: { maakunta, size_ha: sizeHa },
  });
}

// ---------------------------------------------------------------------------
// Markkinayhteenveto dashboard — mirrors backend SMTMarketDashboard.
// Single endpoint returns every aggregate the UI needs.
// ---------------------------------------------------------------------------

export type MarketMode = 'unsold' | 'sold';

export interface MarketKpis {
  avg_count: number;
  avg_ha: number;
  avg_price: number;
  avg_eur_ha: number;
  n_years: number;
}

export interface MarketQuarter {
  key: string;
  count: number;
  ha: number;
  sum: number;
}

export interface MarketRegionTop {
  region: string;
  count: number;
}

export interface MarketYearPanel {
  year: number;
  count: number;
  total_ha: number;
  total_sum: number;
  median_eur_ha: number;
  median_m3_per_ha: number;
  median_tukki_pct: number;
  top_regions: MarketRegionTop[] | null;
  inst_count: number;
  priv_count: number;
}

export interface MarketRegionRow {
  region: string;
  count: number;
  ha: number;
  sum: number;
  eur_ha: number;
}

export interface MarketBuyerYear {
  year: number;
  inst_n: number;
  priv_n: number;
}

export interface MarketDashboard {
  mode: MarketMode;
  year_min: number;
  year_max: number;
  kpis: MarketKpis;
  quarters: MarketQuarter[] | null;
  years: MarketYearPanel[] | null;
  regions: MarketRegionRow[] | null;
  buyer_mix: MarketBuyerYear[] | null;
}

export interface DashboardFilters {
  mode?: MarketMode;
  regions?: string[];
  kunta?: string;
  min_ha?: number | null;
  max_ha?: number | null;
  min_m3?: number | null;
  min_price?: number | null;
}

/** Fetch the full Markkinayhteenveto aggregate (level≥2 required). */
export async function getMarketDashboard(filters: DashboardFilters = {}): Promise<MarketDashboard> {
  return requestData<MarketDashboard>('/smt/market/dashboard', {
    query: {
      mode: filters.mode,
      regions: filters.regions && filters.regions.length > 0 ? filters.regions.join(',') : undefined,
      kunta: filters.kunta || undefined,
      min_ha: filters.min_ha ?? undefined,
      max_ha: filters.max_ha ?? undefined,
      min_m3: filters.min_m3 ?? undefined,
      min_price: filters.min_price ?? undefined,
    },
  });
}
